import React, { useCallback, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MainLayout from "../../../../layouts/MainLayout";
import "../../../../assets/styles/custom-styles.css";
import Form from "../../../../components/forms/Form";
import { contactFields } from "../../../../constants/forms/contactFields";
import useLoader from "../../../../hooks/useLoader";
import useNotification from "../../../../hooks/useNotification";
import useForm from "../../../../hooks/useForm";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../utils/objectUtils";
import useBaseService from "../../../../hooks/services/useBaseService";
import { entities } from "../../../../constants/entities";
import PageHeader from "../../../../components/PageHeader";
import SectionHeader from "../../../../components/forms/SectionHeader";
import SimpleBody from "../../../../components/forms/SimpleBody";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useUrlFilters from "../../../../hooks/useUrlFilters";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";

const EditSupplierContactPage = () => {
  const navigate = useNavigate();
  const { id, contactId } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { filters: contactFilters, pagination: contactPagination } =
    useUrlFilters(
      {
        deleted_at: false,
      },
      `almox_supplierContacts`,
    );

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const {
    getByColumn: fetchById,
    put: putRequest,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const { formData, handleChange, formatData } = useForm(
    setDefaultFieldValues(contactFields),
  );

  // --- Query para buscar os detalhes do Contato ---
  const {
    data: contactData,
    isLoading: isLoadingContact,
    isError: isErrorContact,
    error: contactQueryError,
  } = useQueryWithLoader({
    queryKey: ["almox_supplierContact", id, contactId],
    queryFn: async () => {
      showLoader();
      const response = await fetchById(
        entities.suppliers.contacts.getById(id, contactId),
      );
      return response.result;
    },
    staleTime: 5 * 60 * 1000, // 5 minutos de cache
  });

  // --- useEffect para carregar os detalhes do contato ---
  useEffect(() => {
    if (
      !isLoadingContact &&
      !isErrorContact &&
      !contactQueryError &&
      contactData
    ) {
      hideLoader();
      formatData(contactData, contactFields);
    }
    if (isErrorContact || contactQueryError) {
      hideLoader();
      showNotification(
        "error",
        `Erro ao carregar contato: ${
          contactQueryError.response?.data?.message || contactQueryError.message
        }`,
      );
      navigate(`/almoxarifado/fornecedores/detalhes/${id}`, {
        replace: true,
        state: {
          type: "error",
          message: "Contato não encontrado ou erro ao carregar.",
        },
      });
    }
  }, [isLoadingContact, isErrorContact, contactQueryError, contactData]);

  // --- Mutação para atualizar o Contato ---
  const updateContactMutation = useMutation({
    mutationFn: async (dataToUpdate) => {
      showLoader();
      setFormErrors({});
      const response = await putRequest(
        entities.suppliers.supplierContactUpdate(id, contactId),
        dataToUpdate,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        navigate(`/almoxarifado/fornecedores/detalhes/${id}`, {
          state: { type: "success" },
          replace: true,
        });
        queryClient.invalidateQueries({
          queryKey: ["almox_supplierContact", id, contactId],
        });
        queryClient.invalidateQueries({
          queryKey: [
            "almox_supplierContacts",
            id,
            contactFilters,
            contactPagination,
          ],
        });
      }
    },
    onError: (err) => {
      console.log("onError ", err);
      if (err && err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      await updateContactMutation.mutateAsync(formData);
    } catch (error) {
      console.error("Erro ao enviar contato:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, updateContactMutation]);

  const handleBack = useCallback(() => {
    navigate(`/almoxarifado/fornecedores/detalhes/${id}/`);
  }, [navigate, id]);

  // useEffect para mensagens de estado de localização
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  const renderForm = () => {
    const renderedSections = new Set();

    return contactFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={section.fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleChange}
          />
        </div>
      );
    });
  };

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Editar Contato do Fornecedor"
        showBackButton={true}
        backUrl={`/almoxarifado/fornecedores/detalhes/${id}/`}
      />

      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Salvar Alterações"
          textLoadingSubmit="Salvando..."
          handleBack={handleBack}
          isLoading={isLoadingContact || updateContactMutation.isPending}
        >
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditSupplierContactPage;
